import { useEffect, useRef, useState } from "react";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaperPlaneRight } from "@phosphor-icons/react";

export default function MessageThread({ clientId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await http.get(`/messages/${clientId}`);
      setMessages(data);
    } catch (e) {
      setErr(apiError(e));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [clientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await http.post(`/messages/${clientId}`, { body: body.trim() });
      setBody("");
      await load();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="message-thread">
      <div className="border border-[#262626] bg-[#0A0A0A] h-80 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-600 text-sm font-manrope">
            No messages yet. Say hi.
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[80%] px-4 py-2 ${mine ? "bg-[#FF3333] text-white" : "bg-[#1A1A1A] text-zinc-200 border border-[#262626]"}`}>
                <div className="text-[10px] uppercase tracking-[0.15em] opacity-70 mb-1">
                  {m.sender_role === "coach" ? "Coach" : m.sender_name || "You"}
                </div>
                <div className="text-sm font-manrope whitespace-pre-wrap break-words">{m.body}</div>
                <div className="text-[10px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333] mt-2">{err}</div>}

      <form onSubmit={send} className="mt-3 flex gap-2" data-testid="message-form">
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message..."
          className="id-input flex-1"
          data-testid="message-input"
        />
        <button type="submit" disabled={sending || !body.trim()} className="id-btn-primary" data-testid="send-message-button">
          <PaperPlaneRight size={16} weight="bold" />
        </button>
      </form>
    </div>
  );
}
